import { CompartmentView, LinkView, Pagination } from '@ekkogmbh/apisdk';
import { Add, CloudUpload, Delete, Edit, Highlight, Replay, SaveAlt } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { ColumnDef, DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { LinkPanel } from '../../LabelManagement/Components/LinkPanel';
import { ExportPanel } from './ExportPanel';
import React from 'react';
import { SelectedCompartmentsDialog } from './SelectedCompartmentsDialog';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ObjectField, { ObjectFieldWrapperProps } from 'src/Common/Components/DataTable/ObjectField';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import { LinkChip } from './LinkChip';
import { CompartmentPanel } from './CompartmentPanel';
import { ImportPanel } from './ImportPanel';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import { convertCoordinateToPath } from 'src/Common/Helper/Nodes';
import useDataTable from 'src/Common/Providers/DataTableProvider';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import { useNavigate } from 'react-router';
import usePagination from 'src/Common/Providers/PaginationProvider';
import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';

// Matches the LinkChip container height: chip height (48) + chip margin
// (8 top + 8 bottom) = 64px, so both columns align their scrollbars.
const ScrollableFieldWrapper = (props: ObjectFieldWrapperProps): React.JSX.Element => (
  <ScrollableStack minHeight={64} width="100%" {...props} />
);

export const CompartmentManagementContent = (): React.JSX.Element => {
  const { selectedData, isFilterSelect, totalItemCount } = useDataTable();
  const { scrollTop } = useNavigation();
  const { subscribe } = useTaskCollections();
  const navigate = useNavigate();
  const { api, hasPermission } = useEkko();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();

  const [isRequesting, startRequest] = useRequest();
  const [openPanel, setOpenPanel] = useState<'link' | 'compartment' | 'import' | 'export'>();
  const [openDialog, setOpenDialog] = useState<'delete' | 'bulkDelete' | 'deleteLink' | 'sync' | 'blink'>();
  const [selectedCompartment, setSelectedCompartment] = useState<CompartmentView>();
  const [selectedLink, setSelectedLink] = useState<LinkView>();

  const dismiss = useCallback(() => {
    setOpenPanel(undefined);
    setOpenDialog(undefined);
    setSelectedCompartment(undefined);
    setSelectedLink(undefined);
  }, []);

  const fetchCompartments = useCallback(
    async (pagination: Pagination) => await api.getCompartments(pagination),
    [api.getCompartments],
  );

  const deleteLink = useCallback(
    (link: LinkView) => {
      startRequest(
        () => api.deleteLink(link, subscribe),
        () => {
          enqueueSnackbar('Link deleted');
          dismiss();
          refresh();
        },
      );
    },
    [api.deleteLink],
  );

  const deleteCompartment = useCallback(
    (compartment: CompartmentView) => {
      startRequest(
        () => api.deleteCompartment(compartment, subscribe),
        () => {
          enqueueSnackbar('Compartment deleted');
          dismiss();
          refresh();
        },
      );
    },
    [api.deleteCompartment],
  );

  const blinkCoordinate = useCallback(
    (coordinate: string) => {
      startRequest(
        () => api.blinkByCoordinate(coordinate),
        () => {
          enqueueSnackbar('Flashing lights at coordinate: ' + coordinate);
          dismiss();
        },
      );
    },
    [api.blinkByCoordinate],
  );

  const dialog = useMemo(() => {
    switch (openDialog) {
      case 'delete':
        return (
          <GenericDialog
            open
            type="confirmation"
            title={'Delete Compartment'}
            text={`Delete Compartment with Coordinate ${selectedCompartment!.coordinate}?`}
            onClose={dismiss}
            onConfirm={async () => {
              deleteCompartment(selectedCompartment!);
            }}
            timedOkButton={true}
          />
        );
      case 'bulkDelete':
      case 'sync':
        const variant = openDialog === 'sync' ? openDialog : 'delete';
        return <SelectedCompartmentsDialog variant={variant} onDismiss={dismiss} />;
      case 'blink':
        const blinkDialogText = selectedCompartment
          ? `Blink all Label linked to Coordinate ${selectedCompartment!.coordinate}?`
          : '';
        return (
          <GenericDialog
            open
            type={'confirmation'}
            title={'Blink by Coordinate'}
            text={blinkDialogText}
            onClose={dismiss}
            onConfirm={() => {
              blinkCoordinate(selectedCompartment!.coordinate);
            }}
            timedOkButton={false}
          />
        );
      case 'deleteLink':
        if (selectedLink === undefined) {
          return <></>;
        }

        const deleteLinkDialogText = (
          <React.Fragment>
            Coordinate: <span style={{ fontWeight: 'bold' }}>{selectedLink.coordinate}</span>
            <br />
            LabelId: <span style={{ fontWeight: 'bold' }}>{selectedLink.labelId}</span>
            <br />
            Template: <span style={{ fontWeight: 'bold' }}>{selectedLink.template.name}</span>
            <br />
            Page: <span style={{ fontWeight: 'bold' }}>{selectedLink.pageNumber}</span>
          </React.Fragment>
        );

        return (
          <GenericDialog
            open
            type="confirmation"
            title={'Delete Link'}
            text={deleteLinkDialogText}
            onClose={dismiss}
            onConfirm={async () => {
              deleteLink(selectedLink);
            }}
            timedOkButton={true}
          />
        );
      default:
        return undefined;
    }
  }, [openDialog, selectedCompartment, selectedLink, dismiss, deleteCompartment, deleteLink, blinkCoordinate]);

  const panel = useMemo(() => {
    switch (openPanel) {
      case 'compartment':
        return <CompartmentPanel compartment={selectedCompartment} closeHandler={dismiss} />;
      case 'link':
        return <LinkPanel link={selectedLink} compartment={selectedCompartment!} closeHandler={dismiss} />;
      case 'export':
        return <ExportPanel closeHandler={dismiss} />;
      case 'import':
        return <ImportPanel closeHandler={dismiss} />;
      default:
        return undefined;
    }
  }, [openPanel, selectedCompartment, selectedLink]);

  /** open/close panel when we change the content */
  useEffect(() => {
    setPanelOpen(panel !== undefined);
  }, [panel]);

  const buttons = useMemo(() => {
    const btns = [
      <ContentControlButton
        key={'add'}
        content={<Add />}
        onClick={() => {
          dismiss();
          setOpenPanel('compartment');
          scrollTop();
        }}
        tooltip={'Add Compartment'}
        requiredPermission={Permissions.SHELFLABELING_WRITE}
      />,
      <ContentControlButton
        key={'import'}
        content={<CloudUpload />}
        onClick={() => {
          dismiss();
          setOpenPanel('import');
          scrollTop();
        }}
        tooltip={'Import'}
      />,
      <ContentControlButton
        key={'export'}
        content={<SaveAlt />}
        onClick={() => {
          dismiss();
          setOpenPanel('export');
          scrollTop();
        }}
        tooltip={'Export'}
      />,
    ];

    const selectedCount = isFilterSelect ? totalItemCount : selectedData.length;
    if (selectedCount > 0) {
      btns.push(
        <ContentControlButton
          key={'sync'}
          content={<Replay />}
          onClick={() => {
            dismiss();
            setOpenDialog('sync');
          }}
          tooltip={'Synchronize Devices'}
          flipText={selectedCount.toString()}
        />,
        <ContentControlButton
          key={'bulkDelete'}
          content={<Delete />}
          onClick={() => {
            dismiss();
            setOpenDialog('bulkDelete');
          }}
          tooltip={'Delete Compartments'}
          flipText={selectedCount.toString()}
        />,
      );
    }

    return btns;
  }, [selectedData, isFilterSelect, totalItemCount, dismiss, setOpenDialog, setOpenPanel]);

  const isEditDisabledFn = useCallback(
    (c: CompartmentView): boolean =>
      isRequesting || hasPermission(Permissions.SHELFLABELING_WRITE, convertCoordinateToPath(c.coordinate ?? '')),
    [hasPermission, isRequesting],
  );

  const columns = useMemo(
    (): ColumnDef[] => [
      {
        header: 'Coordinate',
        body: (c, searchText) => (
          <ScrollableStack direction={'row'} width={'100%'} minHeight={64} alignItems={'center'}>
            {searchText ? dataTableHighlight(c.coordinate, [searchText]) : c.coordinate}
          </ScrollableStack>
        ),
        filterField: 'identifier',
        sortField: 'Node.materializedPath',
        proportionalContent: '25%',
      },
      {
        header: 'Fields',
        body: (c: CompartmentView, searchText?: string) => {
          const fields = Object.keys(c.fields).map((fieldKey) => ({
            key: fieldKey,
            value: c.fields[fieldKey],
          }));

          let fieldSearch: string[] | undefined;
          fieldSearch = searchText?.split(';;');
          if (fieldSearch && fieldSearch.length >= 1) {
            const fieldValues: string[] = fieldSearch.reduce((carry: string[], f) => {
              const [k, v] = f.split('::');
              if (k && v) {
                // key::value format - highlight the value part
                carry.push(v);
              } else if (k) {
                // plain search term - highlight it directly
                carry.push(k);
              }

              return carry;
            }, []);
            fieldSearch = fieldValues;
          }

          return (
            <ObjectField
              headlined
              direction={'row'}
              fields={fields}
              searchText={fieldSearch}
              useWrapper={ScrollableFieldWrapper}
              extraEntryStyle={{ margin: 8 }}
            />
          );
        },
        proportionalContent: '50%',
        filterField: 'fields',
        searchTooltip: (
          <ObjectField
            headlined
            fields={[
              {
                key: 'Search for specific key-value pairs:',
                value: '{key}::{value};;*::{wildcard};;...',
              },
            ]}
          />
        ),
      },
      {
        header: 'Links',
        searchTooltip: 'Case-Sensitive',
        body: (c: CompartmentView, searchText?: string) => {
          const entries = c.links.map((link: LinkView, index: number) => (
            <LinkChip
              key={link.id}
              link={link}
              isProcessing={false} // @todo
              dataIndex={index}
              onClick={() => {
                setSelectedCompartment(c);
                setSelectedLink(link);
                setOpenPanel('link');
                scrollTop();
              }}
              onDelete={() => {
                setSelectedLink(link);
                setOpenDialog('deleteLink');
              }}
              highlightText={searchText}
            />
          ));

          return (
            <ScrollableStack direction={'row'} width={'100%'} minHeight={64}>
              {entries}
            </ScrollableStack>
          );
        },
        filterField: 'link',
        proportionalContent: '25%',
      },
    ],
    // All closure deps (setters, navigate, scrollTop) are stable references —
    // this memo effectively never recomputes unless navigation helpers change.
    [scrollTop, setSelectedCompartment, setSelectedLink, setOpenPanel, setOpenDialog],
  );

  const actions = useMemo(
    () => [
      // NOTE: 2026-02-19: Disables but for now left within the codebase are we
      // are not 100% sure no customer is using this. It is however quite
      // likely nobody will ask for this. Then it can be removed in the future
      // {
      //   title: 'Details',
      //   icon: <Assignment />,
      //   onClick: (c: CompartmentView) => {
      //     dismiss();
      //     navigate(c.coordinate);
      //   },
      //   disabled: (c: CompartmentView) => c.operationCount < 1,
      // },
      {
        title: 'Add Link',
        icon: <LinkIcon />,
        onClick: (c: CompartmentView) => {
          dismiss();
          setSelectedCompartment(c);
          setSelectedLink(undefined);
          setOpenPanel('link');
          scrollTop();
        },
        disabled: isEditDisabledFn,
      },
      {
        title: 'Edit',
        icon: <Edit />,
        onClick: (c: CompartmentView) => {
          dismiss();
          setSelectedCompartment(c);
          setOpenPanel('compartment');
          scrollTop();
        },
        disabled: isEditDisabledFn,
      },
      {
        title: 'Blink',
        icon: <Highlight />,
        onClick: (c: CompartmentView) => {
          dismiss();
          setSelectedCompartment(c);
          setOpenDialog('blink');
        },
        disabled: isEditDisabledFn,
      },
      {
        title: 'Delete',
        icon: <Delete />,
        onClick: (c: CompartmentView) => {
          dismiss();
          setSelectedCompartment(c);
          setOpenDialog('delete');
        },
        disabled: isEditDisabledFn,
      },
    ],
    [
      dismiss,
      navigate,
      isEditDisabledFn,
      scrollTop,
      setSelectedCompartment,
      setSelectedLink,
      setOpenPanel,
      setOpenDialog,
    ],
  );

  return (
    <ContentControl dialog={dialog} panel={panel} buttons={buttons}>
      <DataTable selectableRows fetchItems={fetchCompartments} columns={columns} actions={actions} />
    </ContentControl>
  );
};
